import { z as zod } from "zod";

export function createDynamicStringValidator(
  allowedValues: string[]
): zod.ZodEffects<zod.ZodString, string, string> {
  return zod.string().refine((value) => allowedValues.includes(value), {
    message: `Invalid value. Must be one of: ${allowedValues.join(', ')}`
  });
}

// Validates the supported US phone number formats:
//   +1 (555) 123-4567
//   1-555-123-4567
//   555-123-4567
//   5551234567
// Ported verbatim from surepass-react-native's utls/index.ts so web and
// mobile agree on what counts as a valid phone number.
export function isValidPhone(phone: string): boolean {
  const trimmed = String(phone).trim();
  // Only allow digits, an optional leading +, and separators (spaces, parens, hyphens)
  if (!/^\+?[\d\s()-]+$/.test(trimmed)) {
    return false;
  }
  const digits = trimmed.replace(/\D/g, '');
  // Drop the optional US country code (a leading 1) to get the national number.
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  // A US national number is 10 digits with an area code starting 2-9. This
  // rejects an 11-digit number missing a digit (e.g. 1 720 666-666 -> 1720666666,
  // which is only 10 digits with a leading 1).
  return national.length === 10 && /^[2-9]/.test(national);
}

// Strips everything except digits from a phone number, for submission to the API.
export function stripPhoneNumber(phone: string): string {
  return String(phone).replace(/\D/g, '');
}

// Unlike the mobile app, phone number is optional everywhere in the web app,
// so an empty value is always valid; the format is only checked when present.
export function createOptionalPhoneValidator(): zod.ZodEffects<zod.ZodString, string, string> {
  return zod.string().refine((value) => value === '' || isValidPhone(value), {
    message: 'Please enter a valid phone number'
  });
}